import { useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { CustomerId } from "@yalana/contracts";
import { useAuth } from "../../features/auth";
import { customersQueries } from "../queries/customers.queries";

/**
 * The customer the current screen is operating on.
 *
 * BEL-730: forms reached from `/app/c/<slug>/…` used to fill `customerId` from
 * `user.customerIds[0]`, so the URL named one customer and the row was written
 * to another — silently, with no selector to correct it. The slug in the route
 * is the authority when there is one; the single-membership fallback only
 * covers the `/app/admin/…` screens, which carry no slug.
 *
 * `createBike` already derives its customer server-side from the location it
 * was given, which is stronger than anything the client can promise. This hook
 * is for the entities with no such parent — and those services now refuse a
 * customer the caller cannot reach, so a wrong value fails loudly rather than
 * writing across.
 */
export function useScopedCustomerId(): CustomerId | undefined {
  const { user } = useAuth();
  // `strict: false` so this works on the `/app/admin/…` routes too, which have
  // no `customerSlug` param at all.
  const params = useParams({ strict: false }) as {
    customerSlug?: string;
    // BEL-765: the legacy `/app/$customer/…` route tree (WelcomePage) names
    // this param `customer`, not `customerSlug` — read both before falling
    // back to single-membership resolution.
    customer?: string;
  };
  const slug = params?.customerSlug ?? params?.customer;

  const { data: customers } = useQuery({
    ...customersQueries.list(),
    enabled: !!slug,
  });

  if (slug) {
    const match = (customers ?? []).find((c) => c.slug === slug);
    return match ? (match.id as CustomerId) : undefined;
  }

  return user?.customerIds?.length === 1
    ? (user.customerIds[0] as CustomerId)
    : undefined;
}
